/**
 * Lightweight adapter over native EventTarget that provides
 * Node.js EventEmitter-compatible interface (.emit(), .on(), .off(), .once()).
 *
 * Used as a drop-in replacement for the npm "events" package
 * in upload adapters, strategies, and as a safe stub in TwilioProvider.
 *
 * Unlike SyntheticEventEmitter (which dispatches globally on window),
 * NativeEventEmitter is instance-scoped — each instance maintains
 * its own isolated set of listeners.
 */
class NativeEventEmitter extends EventTarget {
  private listenerMap = new Map<string, Map<(...args: unknown[]) => void, EventListener>>();

  emit(event: string, ...args: unknown[]): boolean {
    const eventListeners = this.listenerMap.get(event);
    const hasListeners = eventListeners !== undefined && eventListeners.size > 0;

    this.dispatchEvent(new CustomEvent(event, { detail: args }));
    return hasListeners;
  }

  on(event: string, callback: (...args: unknown[]) => void): this {
    const wrappedListener: EventListener = (e: Event) => {
      const detail = (e as CustomEvent).detail as unknown[];
      callback(...detail);
    };

    if (!this.listenerMap.has(event)) {
      this.listenerMap.set(event, new Map());
    }
    this.listenerMap.get(event)!.set(callback, wrappedListener);

    this.addEventListener(event, wrappedListener);
    return this;
  }

  once(event: string, callback: (...args: unknown[]) => void): this {
    const onceCallback = (...args: unknown[]): void => {
      this.off(event, onceCallback);
      callback(...args);
    };

    return this.on(event, onceCallback);
  }

  off(event: string, callback: (...args: unknown[]) => void): this {
    const eventListeners = this.listenerMap.get(event);
    if (eventListeners) {
      const wrappedListener = eventListeners.get(callback);
      if (wrappedListener) {
        this.removeEventListener(event, wrappedListener);
        eventListeners.delete(callback);
      }
    }
    return this;
  }

  removeListener(event: string, callback: (...args: unknown[]) => void): this {
    return this.off(event, callback);
  }

  /**
   * No-op for Node.js EventEmitter API compatibility.
   * Native EventTarget has no listener limit.
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setMaxListeners(_: number): this {
    return this;
  }
}

export default NativeEventEmitter;
